import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

import { H4 } from '@/components/UI/Typography/Heading';
import { Text } from '@/components/UI/Typography/Text';

export const TitleBar = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-direction: row;
  font-size: ${(p) => (p.isMobile ? p.theme.fontSizes.md : p.theme.fontSizes.lg)};
  font-weight: 400;

  strong {
    font-weight: 700;
  }

  @media (max-width: 639px) {
    align-items: flex-start;
    padding-bottom: 8px;
    border-bottom: 1px solid ${(p) => p.theme.colors.gray100};
    line-height: 1.35;
  }
`;

export const DashboardLayout = styled.div<{ isTablet: boolean }>`
  width: 100%;
  min-width: 0;

  @media (max-width: 639px) {
    margin-top: 20px;
  }
`;

export const MainStack = styled.div`
  display: grid;
  gap: 18px;
  min-width: 0;

  @media (max-width: 639px) {
    gap: 16px;
  }
`;

export const ContentPanel = styled.section`
  min-width: 0;
  padding: 24px;
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};

  @media (max-width: 639px) {
    padding: 24px 12px;
    border-radius: ${(p) => p.theme.radii.xl};
  }
`;

export const SectionHeader = styled(H4)`
  margin: 0 0 16px;
  color: ${(p) => p.theme.colors.primary500};
  font-weight: 700;

  @media (max-width: 639px) {
    margin-bottom: 16px;
    font-size: 16px;
    line-height: 1.35;
  }
`;

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  min-width: 0;

  @media (max-width: 639px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
`;

const summaryVariantStyle = {
  neutral: css`
    border-left-color: #616161;
  `,
  primary: css`
    border-left-color: #0b319d;
  `,
  warning: css`
    border-left-color: #fcb41d;
  `,
};

export const SummaryCard = styled.article<{ variant?: string }>`
  position: relative;
  min-width: 0;
  min-height: 98px;
  padding: 16px;
  border-left: 4px solid ${(p) => p.theme.colors.gray300};
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.gray000};
  ${(p) =>
    p.variant && p.variant in summaryVariantStyle
      ? summaryVariantStyle[p.variant as keyof typeof summaryVariantStyle]
      : ''}

  @media (max-width: 639px) {
    min-height: 98px;
    padding: 14px 12px;
    border-left-width: 3px;
  }
`;

export const SummaryCardLink = styled(Link)`
  display: block;
  min-height: 66px;
  margin: -16px;
  padding: 16px 48px 16px 16px;
  border-radius: ${(p) => p.theme.radii.base};
  color: inherit;
  text-decoration: none;
  transition: background-color 0.2s ease;

  svg {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 22px;
    height: 22px;
  }

  &:hover {
    background: ${(p) => p.theme.colors.gray100};
  }

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primary500};
    outline-offset: 2px;
  }

  @media (max-width: 639px) {
    min-height: 70px;
    margin: -14px -12px;
    padding: 14px 34px 12px 12px;

    svg {
      top: 12px;
      right: 10px;
      width: 18px;
      height: 18px;
    }
  }
`;

export const SummaryValue = styled.div`
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.lg};
  font-weight: 700;
  line-height: 1;

  @media (max-width: 639px) {
    font-size: 34px;
  }
`;

export const SummaryLabel = styled(Text)`
  margin: 10px 0 0;
  color: ${(p) => p.theme.colors.gray300};
  overflow-wrap: anywhere;

  @media (max-width: 639px) {
    margin-top: 10px;
    font-size: 14px;
    line-height: 1.25;
  }
`;

export const ItemList = styled.ul`
  display: grid;
  gap: 10px;
  padding: 0;
  margin: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 14px 0;
    border-bottom: 1px solid ${(p) => p.theme.colors.gray100};
  }

  li:last-child {
    border-bottom: 0;
  }

  @media (max-width: 639px) {
    gap: 0;

    li {
      align-items: flex-start;
      flex-direction: column;
      gap: 8px;
      padding: 8px 0;
    }
  }
`;

export const ItemBody = styled.div`
  min-width: 0;
`;

export const ItemTitle = styled(Text)`
  margin: 0 0 5px;
  color: ${(p) => p.theme.colors.gray500};
  font-weight: 700;
  overflow-wrap: anywhere;

  @media (max-width: 639px) {
    font-size: 14px;
  }
`;

export const ItemMeta = styled(Text)`
  margin: 0;
  color: ${(p) => p.theme.colors.gray300};
  overflow-wrap: anywhere;

  @media (max-width: 639px) {
    font-size: 12px;
  }
`;

export const ActionButton = styled.button`
  min-width: 86px;
  min-height: 36px;
  padding: 8px 14px;
  border: 1px solid ${(p) => p.theme.colors.primary500};
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.light};
  color: ${(p) => p.theme.colors.primary500};
  font: inherit;
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.colors.primary500};
    color: ${(p) => p.theme.colors.light};
  }

  @media (max-width: 639px) {
    min-height: 34px;
    padding: 6px 12px;
    font-size: 14px;
  }
`;

export const EmptyText = styled(Text)`
  margin: 0;
  color: ${(p) => p.theme.colors.gray300};

  @media (max-width: 639px) {
    font-size: 16px;
    line-height: 1.35;
  }
`;

export const StorageUsageList = styled.div`
  display: grid;
  gap: 18px;
`;

export const StorageUsageHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;

  @media (max-width: 560px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }
`;

export const StorageUsageName = styled(Text)`
  margin: 0;
  color: ${(p) => p.theme.colors.gray500};
  font-weight: 700;
`;

export const StorageUsageValue = styled(Text)`
  margin: 0;
  color: ${(p) => p.theme.colors.gray300};
`;

export const StorageProgress = styled.div<{ $exceeded: boolean }>`
  height: 10px;
  overflow: hidden;
  border-radius: 4px;
  background: ${(p) => p.theme.colors.gray100};

  span {
    display: block;
    width: var(--usage-percentage);
    height: 100%;
    background: ${(p) => (p.$exceeded ? '#C62828' : '#0B319D')};
  }
`;

export const StorageWarning = styled(Text)`
  margin: 8px 0 0;
  color: #b42318;
  font-weight: 700;
`;
